
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import globalHome from "../Assets/giicon-5.png";
import globalHome2 from "../Assets/giicon-6.png";
import globalHome3 from "../Assets/giicon-8.png";
import globalHome4 from "../Assets/giicon-9.png";
import { amplitude } from "../../analytics";

// ── Brand palette ─────────────────────────────────────────────────
const C = {
  primary: "#0A2E8A",
  primaryDark: "#061E5C",
  primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC",
  primarySofter: "#F2F5FE",
  secondary: "#10682A",
  bg: "#FAFBFC",
  paper: "#FFFFFF",
  border: "#E5E9F0",
  textPrimary: "#0D1117",
  textSecondary: "#5B6B7E",
  textMuted: "#8A9BB0",
  error: "#DC2626",
  success: "#0A7D34",
  // Admin accent — distinct from primary navy and secondary green so it's
  // immediately recognisable as "you're in the admin section now"
  admin: "#0A7D34",
  adminDark: "#5C1F0C",
  adminSoft: "#FEE7DC",
};

const loginImages = [globalHome, globalHome2, globalHome3, globalHome4];
const randomImage = loginImages[Math.floor(Math.random() * loginImages.length)];

const API = "https://backend-global-1vq9.onrender.com";

function Login() {
  const navigate = useNavigate();
  const routerLocation = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(routerLocation.state?.notice || "");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [resending, setResending] = useState(false);

  // ── NEW: superuser-mode toggle ──
  // Same auth API, just visually distinct entry point. The login response's
  // role drives the redirect — if a non-superuser logs in here, they're
  // still routed to their own dashboard (no security implication).
  const [adminMode, setAdminMode] = useState(false);

  const handleTogglePassword = () => setShowPassword(!showPassword);

  // Switch between standard and admin mode. Clears feedback so error states
  // don't carry over awkwardly between the two modes.
  const toggleAdminMode = () => {
    setAdminMode((prev) => !prev);
    setError("");
    setInfo("");
    setNeedsVerify(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setNeedsVerify(false);
    setLoading(true);

    const payload = { email, password };

    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.unverified) {
          setNeedsVerify(true);
          throw new Error(data.msg || "Please verify your email before logging in.");
        }
        throw new Error(
          data.msg || data.message || "Login failed — check credentials or sign up"
        );
      }

      // ── Helpful guard for admin mode ──
      // If someone uses the admin entry but their account isn't superuser,
      // surface a clear error instead of silently bouncing to the user dash.
      if (adminMode && data.user?.role !== "superuser") {
        // Don't persist the token — they didn't intend this login flow
        throw new Error("This account is not an admin account. Use the standard sign-in.");
      }

      if (data.token) {
        localStorage.setItem("token", data.token);
      }
      localStorage.setItem("user", JSON.stringify(data.user));

            // ── Amplitude: tie this device's activity to the real user ──
      amplitude.setUserId(data.user._id);
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set("role", data.user?.role || "unknown");
      amplitude.identify(identifyEvent);

      // Role-based redirect (unchanged)
      if (data.user?.role === "superuser") {
        navigate("/admin/invite-mentor");
      } else if (data.user?.role === "mentor") {
        navigate("/mentor-dash");
      } else if (data.user?.role === "learner") {
        navigate("/learner-dash");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      setError("Enter your email above first, then resend.");
      return;
    }
    setError("");
    setInfo("");
    setResending(true);
    try {
      const res = await fetch(`${API}/api/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      setInfo(
        data.message ||
          "If that account exists and is unverified, a new link has been sent."
      );
    } catch (err) {
      setError("Could not resend verification email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  // ── Visual constants that swap with mode ──
  const heroGradient = adminMode
    ? `linear-gradient(135deg, ${C.admin} 0%, ${C.adminDark} 100%)`
    : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`;
  const heroOrb = adminMode ? `${C.admin}50` : `${C.secondary}40`;
  const heroEyebrow = adminMode ? "Admin Access" : "Sign In";
  const heroTitle = adminMode ? "Welcome, administrator" : "Welcome back";
  const heroSubtitle = adminMode
    ? "Manage mentor invitations, oversee accounts, and configure platform operations."
    : "Access your network, connect with mentors, and collaborate with a thriving community.";
  const submitGradient = adminMode
    ? `linear-gradient(135deg, ${C.admin} 0%, ${C.adminDark} 100%)`
    : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`;
  const submitShadow = adminMode ? `${C.admin}30` : `${C.primary}30`;
  const accentColor = adminMode ? C.admin : C.primary;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", background: C.bg }}>
      {/* Header — gradient + label changes with admin mode */}
      <div
        style={{
          background: heroGradient,
          padding: "80px 20px 50px",
          textAlign: "center",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
          transition: "background 0.3s ease",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-40%",
            right: "-10%",
            width: 280,
            height: 280,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${heroOrb} 0%, transparent 70%)`,
            pointerEvents: "none",
            transition: "background 0.3s ease",
          }}
        />
        <div style={{ position: "relative", zIndex: 1 }}>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.7)",
              marginBottom: 8,
            }}
          >
            {heroEyebrow}
          </p>
          <h1 style={{ fontSize: "2.25rem", fontWeight: 800, marginBottom: 12, letterSpacing: -0.5 }}>
            {heroTitle}
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.85)",
              fontSize: "1rem",
              maxWidth: 500,
              margin: "0 auto",
              lineHeight: 1.5,
            }}
          >
            {heroSubtitle}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleLogin}
        style={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 40,
          padding: "32px 20px",
          flex: 1,
        }}
      >
        {/* Illustration — hide in admin mode to emphasise the focused form */}
        {!adminMode && (
          <img
            src={randomImage}
            style={{ width: 450, maxWidth: "100%", height: "30%", borderRadius: "0.5rem" }}
            alt="illustration"
          />
        )}

        <div
          style={{
            position: "relative",
            flex: adminMode ? "0 1 460px" : "1 1 350px",
            maxWidth: 460,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            background: C.paper,
            padding: 28,
            paddingTop: adminMode ? 32 : 28,
            borderRadius: 16,
            border: `1px solid ${adminMode ? C.adminSoft : C.border}`,
            boxShadow: "0 4px 24px rgba(13,17,23,0.06)",
            alignSelf: "flex-start",
            transition: "border-color 0.3s ease",
            overflow: "hidden",
          }}
        >
          {/* Admin mode indicator strip — communicates "you're in the admin entry" */}
          {adminMode && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${C.admin}, ${C.adminDark})`,
              }}
            />
          )}

          {/* Admin mode badge */}
          {adminMode && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: C.admin,
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: C.admin,
                }}
              >
                Administrator Sign-in
              </span>
            </div>
          )}

          <input
            type="email"
            placeholder={adminMode ? "Admin email" : "Email"}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              ...inputStyle,
              borderColor: adminMode ? C.adminSoft : C.border,
            }}
            required
            autoComplete="email"
          />

          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              ...inputStyle,
              borderColor: adminMode ? C.adminSoft : C.border,
            }}
            required
            autoComplete="current-password"
          />

          <span
            onClick={handleTogglePassword}
            style={{
              fontSize: 12,
              color: accentColor,
              cursor: "pointer",
              textAlign: "right",
              fontWeight: 600,
            }}
          >
            {showPassword ? "Hide" : "Show"} Password
          </span>

          {info && (
            <div
              style={{
                color: C.success,
                fontSize: 13,
                textAlign: "center",
                background: "#ECFDF5",
                padding: 10,
                borderRadius: 8,
                fontWeight: 600,
              }}
            >
              {info}
            </div>
          )}

          {error && (
            <div
              style={{
                color: C.error,
                fontSize: 13,
                textAlign: "center",
                background: "#FEE2E2",
                padding: 10,
                borderRadius: 8,
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}

          {needsVerify && (
            <button
              type="button"
              onClick={handleResend}
              disabled={resending}
              style={{
                background: C.paper,
                border: `1.5px solid ${C.primary}`,
                borderRadius: 10,
                padding: "12px",
                color: C.primary,
                fontWeight: 700,
                fontSize: 14,
                cursor: resending ? "not-allowed" : "pointer",
                width: "100%",
                opacity: resending ? 0.7 : 1,
              }}
            >
              {resending ? "Sending…" : "Resend verification email"}
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              background: loading ? "#CCCCCC" : submitGradient,
              border: "none",
              borderRadius: 10,
              padding: "14px",
              color: "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: loading ? "not-allowed" : "pointer",
              width: "100%",
              transition: "all 0.2s ease",
              boxShadow: loading ? "none" : `0 4px 14px ${submitShadow}`,
            }}
          >
            {loading
              ? "Signing In..."
              : adminMode
                ? "Sign in as Administrator"
                : "Sign In"}
          </button>

          {/* Forgot password only makes sense in normal mode */}
          {!adminMode && (
            <span
              style={{
                color: C.secondary,
                fontSize: 13,
                fontWeight: 700,
                textAlign: "center",
                cursor: "pointer",
              }}
              onClick={() => navigate("/forgot-password")}
            >
              Forgot Password?
            </span>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ color: C.textMuted, fontSize: 12 }}>Or</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>

          {/* Footer actions vary by mode */}
          {adminMode ? (
            <>
              <button
                type="button"
                onClick={toggleAdminMode}
                style={{
                  background: "transparent",
                  border: "none",
                  color: C.primary,
                  fontWeight: 700,
                  fontSize: 13.5,
                  cursor: "pointer",
                  padding: 6,
                }}
              >
                ← Back to standard sign-in
              </button>
              <p style={{
                textAlign: "center",
                fontSize: 11,
                color: C.textMuted,
                margin: 0,
                lineHeight: 1.5,
              }}>
                Admin access is restricted. Unauthorized attempts may be logged.
              </p>
            </>
          ) : (
            <>
              <p style={{ textAlign: "center", fontSize: 14, color: C.textSecondary, margin: 0 }}>
                Don't have an account?{" "}
                <span
                  style={{ color: C.primary, fontWeight: 700, cursor: "pointer" }}
                  onClick={() => navigate("/signup")}
                >
                  Sign Up
                </span>
              </p>

              {/* ── NEW: Superuser entry link ──
                  Subtle, sits below sign-up. Doesn't visually compete with the
                  primary CTA but is easy to spot if you know to look for it. */}
              <div
                style={{
                  marginTop: 4,
                  paddingTop: 12,
                  borderTop: `1px dashed ${C.border}`,
                  textAlign: "center",
                }}
              >
                <button
                  type="button"
                  onClick={toggleAdminMode}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: C.textMuted,
                    fontWeight: 600,
                    fontSize: 12,
                    cursor: "pointer",
                    padding: 6,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    transition: "color 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = C.admin; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = C.textMuted; }}
                >
                  {/* Tiny shield-like SVG so it's visually marked as admin */}
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                  </svg>
                  Sign in as superuser
                </button>
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

const inputStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: 10,
  padding: "12px 14px",
  background: C.paper,
  fontSize: 14,
  color: C.textPrimary,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  transition: "border-color 0.2s",
};

export default Login;